/**
 * Newsletter signup popup: show once, dismiss/subscribe persistence.
 */
const STORAGE_KEY = 'tds-newsletter-popup-v1';
const COOKIE_BANNER_KEY = 'tds-cookie-banner-v1';
const FADE_MS = 380;

function isDesignMode() {
  return typeof window.Shopify !== 'undefined' && window.Shopify.designMode;
}

function getStoredState() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function hasSeenPopup() {
  if (isDesignMode()) return false;
  const state = getStoredState();
  return state === 'dismissed' || state === 'subscribed';
}

function markPopupState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, state);
  } catch {
    /* ignore */
  }
}

function dismissPopup(root) {
  if (root.dataset.subscribed === 'true') {
    markPopupState('subscribed');
  } else {
    markPopupState('dismissed');
  }

  const dialog = root.querySelector('[data-newsletter-popup-dialog]');
  if (!dialog || !dialog.open) {
    root.hidden = true;
    return;
  }

  if (root.classList.contains('newsletter-popup--is-closing')) return;

  let finished = false;
  const complete = () => {
    if (finished) return;
    finished = true;
    dialog.close();
    root.hidden = true;
    root.classList.remove('newsletter-popup--is-closing');
    unlockScroll();
  };

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    complete();
    return;
  }

  const onTransitionEnd = (event) => {
    if (event.target !== dialog || event.propertyName !== 'opacity') return;
    dialog.removeEventListener('transitionend', onTransitionEnd);
    complete();
  };

  dialog.addEventListener('transitionend', onTransitionEnd);
  root.classList.add('newsletter-popup--is-closing');

  setTimeout(() => {
    dialog.removeEventListener('transitionend', onTransitionEnd);
    complete();
  }, FADE_MS);
}

function unlockScroll() {
  document.documentElement.removeAttribute('scroll-lock');
  document.body.style.removeProperty('width');
  document.body.style.removeProperty('position');
  document.body.style.removeProperty('top');
  const scrollY = Number(document.body.dataset.newsletterPopupScrollY || 0);
  window.scrollTo({ top: scrollY, behavior: 'instant' });
  delete document.body.dataset.newsletterPopupScrollY;
}

function cookieBannerIsVisible() {
  const banner = document.querySelector('[data-cookie-banner]');
  return banner && !banner.hidden;
}

function cookieBannerWasDismissed() {
  try {
    return Boolean(localStorage.getItem(COOKIE_BANNER_KEY));
  } catch {
    return true;
  }
}

function openPopup(root) {
  const dialog = root.querySelector('[data-newsletter-popup-dialog]');
  if (!dialog || dialog.open) return;

  const scrollY = window.scrollY;
  document.body.dataset.newsletterPopupScrollY = String(scrollY);
  document.body.style.width = '100%';
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
  document.documentElement.setAttribute('scroll-lock', '');

  dialog.showModal();
  root.hidden = false;
  root.classList.remove('newsletter-popup--is-closing');

  const email = dialog.querySelector('input[type="email"]');
  if (email && !email.disabled) {
    requestAnimationFrame(() => email.focus());
  }
}

function scheduleShow(root) {
  if (hasSeenPopup() || root.dataset.subscribed === 'true') return;

  const delayMs = Number(root.dataset.delayMs || 8000);
  let scheduled = false;

  const tryOpen = () => {
    if (scheduled) return;
    if (hasSeenPopup() || root.dataset.subscribed === 'true') return;
    if (!cookieBannerWasDismissed() && cookieBannerIsVisible()) return;

    scheduled = true;
    openPopup(root);
  };

  const startTimer = () => {
    setTimeout(tryOpen, delayMs);
  };

  if (!cookieBannerWasDismissed() && cookieBannerIsVisible()) {
    document.addEventListener('privacy:confirmed', () => startTimer(), { once: true });
    document.addEventListener('privacy:rejected', () => startTimer(), { once: true });

    const observer = new MutationObserver(() => {
      if (cookieBannerWasDismissed() || !cookieBannerIsVisible()) {
        observer.disconnect();
        startTimer();
      }
    });
    const banner = document.querySelector('[data-cookie-banner]');
    if (banner) observer.observe(banner, { attributes: true, attributeFilter: ['hidden'] });
    return;
  }

  startTimer();
}

function bindFormSubmit(root) {
  const form = root.querySelector('.newsletter-popup__form');
  if (!form) return;

  form.addEventListener('submit', () => {
    markPopupState('subscribed');
  });
}

function init() {
  const root = document.querySelector('[data-newsletter-popup]');
  if (!root) return;

  const dialog = root.querySelector('[data-newsletter-popup-dialog]');
  const closeBtn = root.querySelector('[data-newsletter-popup-close]');
  const dismissBtns = root.querySelectorAll('[data-newsletter-popup-dismiss]');

  if (root.dataset.subscribed === 'true') {
    markPopupState('subscribed');
  }

  bindFormSubmit(root);

  closeBtn?.addEventListener('click', () => dismissPopup(root));
  dismissBtns.forEach((btn) => {
    btn.addEventListener('click', () => dismissPopup(root));
  });

  dialog?.addEventListener('click', (event) => {
    if (event.target === dialog) dismissPopup(root);
  });

  dialog?.addEventListener('close', unlockScroll);

  dialog?.addEventListener('cancel', (event) => {
    event.preventDefault();
    dismissPopup(root);
  });

  if (isDesignMode()) {
    root.hidden = false;
    return;
  }

  if (hasSeenPopup() || root.dataset.subscribed === 'true') {
    if (root.dataset.showSuccess === 'true') {
      openPopup(root);
      return;
    }
    root.hidden = true;
    return;
  }

  scheduleShow(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
